import { createEffect, createMemo, createSignal, For, Show, untrack } from 'solid-js';
import { A, useParams } from '@solidjs/router';
import Text from '../components/Text';
import MinecraftText from '../components/MinecraftText';
import ItemStackView from '../components/ItemStackView';
import SellbotReasonItems from '../components/SellbotReasonItems';
import { useApi, isOk } from '../api';
import { useActiveBot } from '../state/activeBot';
import { useTranslation } from '../hooks/useTranslation';
import { useSellbotReason } from '../hooks/useSellbotReason';
import { useItemName } from '../hooks/useItemName';
import { itemKey, parseItemKey } from '../services/craftingResolver';
import { parseSellbotReasonItems } from '../services/sellbotReasonItems';
import { formatQuantity } from '../services/quantityFormatter';
import { formatDateTime } from '../services/dateFormat';
import type { SellbotTransactionDto, AnkaufTransactionDto, AnkaufTransactionItemDto, SellbotJournalEntryDto } from '../api';
import { sellbotStatusClass } from '../services/sellbotStatus';
import styles from './Sellbot.module.css';

export default function SellbotTransactionDetail() {
    const params = useParams();
    const api = useApi();
    const { t } = useTranslation();
    const { reasonText, reasonItems, itemName } = useSellbotReason();
    const { stackName, stackSize } = useItemName();
    const { botId } = useActiveBot();

    const fmtNum = (n: number) => new Intl.NumberFormat('de-DE').format(n);
    const fmtMoney = (n: number) => `${fmtNum(n)}$`;

    // The physical stacks the seller handed over under this offer match. Rows written before the shop
    // recorded them keep their one sample in snbt/receivedCount, so those become a single stack here.
    const purchaseStacks = (i: AnkaufTransactionItemDto) => {
        if (i.stacks.length > 0)
            return i.stacks.map(s => ({ itemId: s.itemId, count: s.count, damage: s.metadata, snbt: s.snbt ?? undefined }));
        if (i.itemId == null) return [];
        return [{ itemId: i.itemId, count: i.receivedCount ?? i.itemCount, damage: i.metadata ?? 0, snbt: i.snbt ?? undefined }];
    };

    const saleItemStack = (key: string, snbt: string | null | undefined, count: number) => {
        const parsed = parseItemKey(key);
        return parsed ? { itemId: parsed.id, count, damage: parsed.metadata, snbt: snbt ?? undefined } : null;
    };

    const [loading, setLoading] = createSignal(true);
    const [txn, setTxn] = createSignal<SellbotTransactionDto | null>(null);
    const [ankauf, setAnkauf] = createSignal<AnkaufTransactionDto | null>(null);
    const [journal, setJournal] = createSignal<SellbotJournalEntryDto[]>([]);
    const [journalLoading, setJournalLoading] = createSignal(false);

    const saleItems = createMemo<{ key: string; count?: number; snbt?: string | null }[]>(() => {
        const s = txn();
        if (!s || s.reasonCode !== 'sold' || !s.reasonArgsJson) return [];
        try {
            const a = JSON.parse(s.reasonArgsJson);
            if (Array.isArray(a.items) && a.items.length > 0)
                return a.items
                    .filter((i: any) => typeof i?.item === 'string')
                    .map((i: any) => ({ key: String(i.item), count: i.count, snbt: i.snbt }));
            return a.item ? [{ key: String(a.item), count: a.count, snbt: a.snbt }] : [];
        } catch { return []; }
    });

    const load = async () => {
        const id = botId();
        const txnId = params.txnId;
        setLoading(true);
        if (!id || !txnId) { setTxn(null); setAnkauf(null); setLoading(false); return; }

        const res = await api.sellbot.getTransaction(id, txnId);
        if (isOk(res.data)) {
            setTxn(res.data.value.sale ?? null);
            setAnkauf(res.data.value.ankauf ?? null);
        } else {
            setTxn(null); setAnkauf(null);
        }
        setLoading(false);

        setJournal([]);
        setJournalLoading(true);
        const jres = await api.sellbot.getJournal(id, txnId);
        if (isOk(jres.data)) setJournal(jres.data.value ?? []);
        setJournalLoading(false);
    };

    createEffect(() => {
        const id = botId();
        const txnId = params.txnId;
        untrack(() => { void load(); });
        void id; void txnId;
    });

    const journalStepLabel = (step: string): string => {
        const key = 'SELLBOT_STEP_' + step;
        const localized = t(key);
        return localized !== key ? localized : step;
    };

    const jstateClass = (state: string): string => {
        switch (state) {
            case 'Ok': return styles.jdotOk;
            case 'Failed': return styles.jdotFailed;
            default: return styles.jdotIntent;
        }
    };

    const jstateIcon = (state: string): string => {
        switch (state) {
            case 'Ok': return 'fa-check';
            case 'Failed': return 'fa-xmark';
            default: return 'fa-hourglass-half';
        }
    };

    type JournalStack = { stack: { itemId: number; count: number; damage: number; snbt?: string }; label: string };

    // A step lists the stacks it moved when it recorded them; older entries carry one sample keyed by
    // the offer, so those still render as a single line.
    const journalStacks = (e: SellbotJournalEntryDto): JournalStack[] => {
        if (!e.dataJson) return [];
        let d: Record<string, any> = {};
        try { d = JSON.parse(e.dataJson); } catch { return []; }
        if (Array.isArray(d.stacks) && d.stacks.length > 0)
            return d.stacks.map((s: any) => {
                const stack = { itemId: s.itemId, count: s.count, damage: s.damage ?? 0, snbt: s.snbt ?? undefined };
                return { stack, label: `${stackName(stack)} ×${formatQuantity(s.count, stackSize(itemKey(stack.itemId, stack.damage)))}` };
            });

        // The activity feed's item payload, written by the steps that record a whole inventory delta
        // (items held after a take, what a chamber deposit moved) rather than one offer's stacks.
        if (Array.isArray(d.items) && d.items.length > 0)
            return d.items.flatMap((entry: any) => {
                const parsed = parseItemKey(String(entry.item ?? ''));
                if (!parsed) return [];
                const stack = { itemId: parsed.id, count: entry.count ?? 1, damage: parsed.metadata, snbt: entry.snbt ?? undefined };
                return [{ stack, label: `${stackName(stack)} ×${formatQuantity(entry.count ?? 1, stackSize(itemKey(parsed.id, parsed.metadata)))}` }];
            });

        const key = d.itemKey ?? d.item;
        if (!key) return [];
        const parsed = parseItemKey(String(key));
        if (!parsed) return [];
        const totalQty = d.qty ?? d.count;
        return [{
            stack: { itemId: parsed.id, count: d.receivedCount ?? totalQty ?? 1, damage: parsed.metadata, snbt: d.snbt ?? undefined },
            label: itemName(String(key), d.snbt) + (totalQty != null ? ` ×${formatQuantity(totalQty, stackSize(String(key)))}` : ''),
        }];
    };

    // What the visit handed back, read off the return steps the journal recorded. A seller whose
    // delivery was capped sees "bought 7.290, gave the rest back" on the transaction itself instead of
    // having to read the timeline to find out why their stack came home.
    const returns = createMemo(() => journal()
        .filter(e => e.step === 'returned')
        .map(e => {
            let data: Record<string, any> = {};
            try { data = e.dataJson ? JSON.parse(e.dataJson) : {}; } catch { /* keep empty */ }
            return {
                items: parseSellbotReasonItems(e.dataJson),
                reason: data.reason ? reasonText(String(data.reason), e.dataJson, String(data.reason)) : null,
            };
        })
        .filter(r => r.items.length > 0));

    const offerPreviewStack = (i: AnkaufTransactionItemDto) => {
        if (i.offerStack) return { itemId: i.offerStack.itemId, count: 1, damage: i.offerStack.metadata, snbt: i.offerStack.snbt ?? undefined };
        return i.itemId != null ? { itemId: i.itemId, count: 1, damage: i.metadata ?? 0 } : null;
    };

    // One offer match per block: its rule preview on top (hover shows the rules it matches on, open
    // slots and all), every stack that matched it listed underneath.
    const purchaseBlock = (i: AnkaufTransactionItemDto) => (
        <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
            <div class="h hcenter" style={{ 'justify-content': 'space-between', gap: 'var(--spacing-sm)' }}>
                <span class="h hcenter" style={{ gap: 'var(--spacing-sm)' }}>
                    <Show when={offerPreviewStack(i)}>{(stack) => <ItemStackView stack={stack()} showTooltip showCount={false} />}</Show>
                    <span><Show when={i.itemKey}>{itemName(i.itemKey!, i.snbt)} </Show>×{fmtNum(i.itemCount)}</span>
                </span>
                <span>{fmtMoney(i.amountPaid)}</span>
            </div>
            <div class={styles.purchaseStacks}>
                <For each={purchaseStacks(i)}>{(stack) => (
                    <div class="h hcenter" style={{ gap: 'var(--spacing-sm)' }}>
                        <ItemStackView stack={stack} size={24} showTooltip />
                        <span class={styles.purchaseStackName}>{stackName(stack)} ×{fmtNum(stack.count)}</span>
                    </div>
                )}</For>
            </div>
        </div>
    );

    const journalMeta = (e: SellbotJournalEntryDto): string => {
        if (!e.dataJson) return '';
        let d: Record<string, any> = {};
        try { d = JSON.parse(e.dataJson); } catch { return ''; }
        const parts: string[] = [];
        if (d.task) parts.push(String(d.task));
        if (d.dropped != null && d.needed != null) parts.push(t('SELLBOT_JOURNAL_META_DELIVERED', formatQuantity(d.dropped), formatQuantity(d.needed)));
        else if (d.needed != null || d.available != null) parts.push(t('SELLBOT_JOURNAL_META_NEED_HAVE', formatQuantity(d.needed ?? 0), formatQuantity(d.available ?? 0)));
        if (d.amount != null) parts.push(fmtMoney(d.amount));
        if (d.payer) parts.push(String(d.payer));
        if (d.seller) parts.push(String(d.seller));
        if (d.reason) parts.push(reasonText(String(d.reason), e.dataJson, String(d.reason)));
        if (d.error) parts.push(String(d.error));
        return parts.join(' · ');
    };

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <A href="/sellbot/transactions" class="btn btn-secondary"><i class="fa-solid fa-arrow-left" /> <Text val="SELLBOT_BACK_TO_TRANSACTIONS" /></A>
            </div>

            <Show when={!loading()} fallback={<div class={styles.skeletonGrid}><For each={[1, 2, 3]}>{() => <div class={styles.skeletonCard} />}</For></div>}>
                <Show when={txn() || ankauf()} fallback={
                    <div class={styles.empty}>
                        <i class="fa-solid fa-circle-question" style={{ 'font-size': '3rem', opacity: 0.5 }} />
                        <p><Text val="SELLBOT_TXN_NOT_FOUND" /></p>
                        <A href="/sellbot/transactions" class="btn btn-primary"><Text val="SELLBOT_BACK_TO_TRANSACTIONS" /></A>
                    </div>
                }>
                    <div class="fcard v" style={{ gap: 'var(--spacing-lg)' }}>
                        <h1 style={{ margin: 0 }}><Text val={txn() ? 'SELLBOT_DETAIL_TXN_TITLE' : 'SELLBOT_DETAIL_ANKAUF_TITLE'} /></h1>

                        <Show when={txn()}>{(t2) => (
                            <div class={styles.detailGrid}>
                                <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_STATUS" /></span>
                                <span><span class={`${styles.badge} ${sellbotStatusClass(t2().status, styles)}`}>{t('SELLBOT_TXN_' + t2().status)}</span></span>
                                <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_REASON" /></span>
                                <span>{reasonText(t2().reasonCode, t2().reasonArgsJson, t2().reason)}</span>
                                <Show when={saleItems().length > 0}>
                                    <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_ITEM" /></span>
                                    <span>
                                        <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                            <For each={saleItems()}>{(si) => (
                                                <span class="h hcenter" style={{ gap: 'var(--spacing-sm)' }}>
                                                    <Show when={saleItemStack(si.key, si.snbt, si.count ?? 1)}>{(stack) => <ItemStackView stack={stack()} showTooltip />}</Show>
                                                    <span>{itemName(si.key, si.snbt)}{si.count != null ? ` ×${formatQuantity(si.count, stackSize(si.key))}` : ''}</span>
                                                </span>
                                            )}</For>
                                        </div>
                                    </span>
                                </Show>
                                <Show when={reasonItems(t2().reasonArgsJson)}>
                                    {(items) => (
                                        <>
                                            <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_HELD_ITEMS" /></span>
                                            <SellbotReasonItems items={items()} />
                                        </>
                                    )}
                                </Show>
                                <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_PAYER" /></span>
                                <span><MinecraftText text={t2().payerName} /></span>
                                <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_AMOUNT" /></span>
                                <span>{fmtMoney(t2().amount)}</span>
                                <Show when={t2().sellbotGrantId}>
                                    {(grantId) => (
                                        <>
                                            <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_GRANT" /></span>
                                            <A href={`/sellbot/grants/${grantId()}`}><Text val="SELLBOT_DETAIL_OPEN_GRANT" /></A>
                                        </>
                                    )}
                                </Show>
                                <Show when={t2().occupantRealName || t2().occupantName}>
                                    <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_OCCUPANT" /></span>
                                    <span>{t2().occupantRealName ?? <MinecraftText text={t2().occupantName!} />}</span>
                                    <Show when={t2().occupantRealName && t2().occupantName}>
                                        <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_OCCUPANT_NICK" /></span>
                                        <span><MinecraftText text={t2().occupantName!} /></span>
                                    </Show>
                                </Show>
                                <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_CREATED" /></span>
                                <span>{formatDateTime(t2().createdAt)}</span>
                                <Show when={t2().completedAt}>
                                    <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_COMPLETED" /></span>
                                    <span>{formatDateTime(t2().completedAt!)}</span>
                                </Show>
                            </div>
                        )}</Show>

                        <Show when={ankauf()}>{(a) => (
                            <div class={styles.detailGrid}>
                                <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_STATUS" /></span>
                                <span><span class={`${styles.badge} ${sellbotStatusClass(a().status, styles)}`}>{t('SELLBOT_TXN_' + a().status)}</span></span>
                                <Show when={a().reason}>
                                    <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_REASON" /></span>
                                    <span>{reasonText(a().reason, null, a().reason)}</span>
                                </Show>
                                <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_SELLER" /></span>
                                <span><MinecraftText text={a().sellerName} /></span>
                                <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_BATCH_ITEMS" /></span>
                                <span>
                                    <div class="v" style={{ gap: 'var(--spacing-sm)' }}>
                                        <For each={a().items}>{(i) => purchaseBlock(i)}</For>
                                    </div>
                                </span>
                                <Show when={returns().length > 0}>
                                    <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_RETURNED_ITEMS" /></span>
                                    <span>
                                        <div class="v" style={{ gap: 'var(--spacing-sm)' }}>
                                            <For each={returns()}>{(r) => (
                                                <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                                    <SellbotReasonItems items={r.items} />
                                                    <Show when={r.reason}>{(reason) => <span class={styles.purchaseStackName}>{reason()}</span>}</Show>
                                                </div>
                                            )}</For>
                                        </div>
                                    </span>
                                </Show>
                                <span class={styles.detailLabel}>
                                    <Text val={a().items.length > 1 ? 'SELLBOT_DETAIL_BATCH_TOTAL' : 'SELLBOT_DETAIL_AMOUNT'} />
                                </span>
                                <span>{fmtMoney(a().amountPaid)}</span>
                                <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_CREATED" /></span>
                                <span>{formatDateTime(a().createdAt)}</span>
                                <Show when={a().completedAt}>
                                    <span class={styles.detailLabel}><Text val="SELLBOT_DETAIL_COMPLETED" /></span>
                                    <span>{formatDateTime(a().completedAt!)}</span>
                                </Show>
                            </div>
                        )}</Show>

                        <div class={styles.journalSection}>
                            <h3 class={styles.journalTitle}><Text val="SELLBOT_DETAIL_JOURNAL" /></h3>
                            <Show when={!journalLoading()} fallback={<div class={styles.journalLoading}><i class="fa-solid fa-spinner fa-spin fa-2x" /></div>}>
                                <Show when={journal().length > 0} fallback={
                                    <div class={styles.emptySmall}><i class="fa-solid fa-timeline" style={{ 'font-size': '2rem', opacity: 0.4 }} /><p><Text val="SELLBOT_JOURNAL_EMPTY" /></p></div>
                                }>
                                    <div class={styles.timeline}>
                                        <For each={journal()}>{(e) => (
                                            <div class={styles.tlItem}>
                                                <span class={`${styles.tlDot} ${jstateClass(e.state)}`} title={t('SELLBOT_JSTATE_' + e.state)}><i class={`fa-solid ${jstateIcon(e.state)}`} /></span>
                                                <div class={styles.tlBody}>
                                                    <div class={styles.tlHead}>
                                                        <span class={styles.tlStep}>{journalStepLabel(e.step)}</span>
                                                        <span class={styles.tlTime}>{formatDateTime(e.createdAt)}</span>
                                                    </div>
                                                    <For each={journalStacks(e)}>{(it) => (
                                                        <span class="h hcenter" style={{ gap: 'var(--spacing-sm)' }}>
                                                            <ItemStackView stack={it.stack} size={24} showTooltip />
                                                            <span class={styles.tlMeta}>{it.label}</span>
                                                        </span>
                                                    )}</For>
                                                    <Show when={journalMeta(e)}>{(m) => <span class={styles.tlMeta}>{m()}</span>}</Show>
                                                </div>
                                            </div>
                                        )}</For>
                                    </div>
                                </Show>
                            </Show>
                        </div>
                    </div>
                </Show>
            </Show>
        </div>
    );
}
